import React from "react"
import { Pagination, PaginationItem, PaginationLink } from "reactstrap"
import PropTypes from "prop-types"

/**
 * StandardPagination Component
 * A reusable pagination component with smart page display
 * 
 * @param {number} currentPage - Current active page
 * @param {number} totalPages - Total number of pages
 * @param {function} onPageChange - Callback function when page changes
 * @param {number} maxPagesToShow - Maximum number of page buttons to show (default: 7)
 */
const StandardPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  maxPagesToShow = 7,
}) => {
  if (totalPages <= 1) return null

  const renderPaginationItems = () => {
    const items = []
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i} active={i === currentPage}>
            <PaginationLink onClick={() => onPageChange(i)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        )
      }
    } else {
      // Smart pagination with ellipsis
      const leftSiblingIndex = Math.max(currentPage - 1, 1)
      const rightSiblingIndex = Math.min(currentPage + 1, totalPages)
      
      const shouldShowLeftDots = leftSiblingIndex > 2
      const shouldShowRightDots = rightSiblingIndex < totalPages - 1
      
      // Always show first page
      items.push(
        <PaginationItem key={1} active={1 === currentPage}>
          <PaginationLink onClick={() => onPageChange(1)}>
            1
          </PaginationLink>
        </PaginationItem>
      )
      
      // Show left dots if needed
      if (shouldShowLeftDots) {
        items.push(
          <PaginationItem key="left-dots" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        )
      }
      
      // Show middle pages
      const startPage = shouldShowLeftDots ? leftSiblingIndex : 2
      const endPage = shouldShowRightDots ? rightSiblingIndex : totalPages - 1
      
      for (let i = startPage; i <= endPage; i++) {
        items.push(
          <PaginationItem key={i} active={i === currentPage}>
            <PaginationLink onClick={() => onPageChange(i)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        )
      }
      
      // Show right dots if needed
      if (shouldShowRightDots) {
        items.push(
          <PaginationItem key="right-dots" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        )
      }
      
      // Always show last page
      items.push(
        <PaginationItem key={totalPages} active={totalPages === currentPage}>
          <PaginationLink onClick={() => onPageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      )
    }
    
    return items
  }

  return (
    <div className="pagination-container">
      <Pagination>
        <PaginationItem disabled={currentPage <= 1}>
          <PaginationLink
            previous
            onClick={() => onPageChange(currentPage - 1)}
          />
        </PaginationItem>
        
        {renderPaginationItems()}
        
        <PaginationItem disabled={currentPage >= totalPages}>
          <PaginationLink
            next
            onClick={() => onPageChange(currentPage + 1)}
          />
        </PaginationItem>
      </Pagination>
    </div>
  )
}

StandardPagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  maxPagesToShow: PropTypes.number,
}

export default StandardPagination
